import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Bookmark, Mail, MailCheck, Eye, Calendar, ChevronRight, User, Settings } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UserDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [savedArticles, setSavedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newsletterSubscribed, setNewsletterSubscribed] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    
    setNewsletterSubscribed(user.newsletter_subscribed || false);
    fetchSavedArticles();
  }, [user, navigate]);

  const fetchSavedArticles = async () => {
    try {
      const response = await axios.get(`${API}/user/saved-articles`, { withCredentials: true });
      setSavedArticles(response.data);
    } catch (error) {
      console.error('Error fetching saved articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveArticle = async (postId) => {
    try {
      await axios.delete(`${API}/user/save-article/${postId}`, { withCredentials: true });
      setSavedArticles(savedArticles.filter(a => a.post_id !== postId));
      toast.success('Article removed');
    } catch (error) {
      toast.error('Failed to remove article');
    }
  };

  const handleNewsletterToggle = async () => {
    try {
      if (!newsletterSubscribed) {
        await axios.post(`${API}/newsletter/subscribe`, { email: user.email }, { withCredentials: true });
        setNewsletterSubscribed(true);
        toast.success('Subscribed to newsletter!');
      } else {
        setNewsletterSubscribed(false);
        toast.success('Unsubscribed from newsletter');
      }
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="user-dashboard">
      {/* Header */}
      <section className="bg-white py-12 border-b border-primary/10">
        <div className="container-padding">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            <div className="flex items-center gap-4">
              {user.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name} 
                  className="w-16 h-16 rounded-full border-2 border-accent"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-accent flex items-center justify-center text-white text-2xl font-sans font-medium">
                  {user.name?.charAt(0) || 'U'}
                </div>
              )}
              <div>
                <h1 className="text-2xl font-sans font-normal text-primary">
                  Welcome, {user.name?.split(' ')[0]}
                </h1>
                <p className="text-secondary text-sm">{user.email}</p>
              </div>
            </div>
            {user.role === 'admin' && (
              <Link to="/admin">
                <Button className="btn-secondary rounded-none">
                  <Settings className="w-4 h-4 mr-2" />
                  Admin Panel
                </Button>
              </Link>
            )}
          </div>
        </div>
      </section>

      <div className="container-padding py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-8">
            {/* Saved Articles */}
            <div className="dashboard-panel mb-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-sans font-medium text-primary flex items-center gap-2">
                  <Bookmark className="w-5 h-5 text-accent" />
                  Saved Articles
                </h2>
                <span className="text-secondary text-sm">{savedArticles.length} articles</span>
              </div>

              {loading ? (
                <div className="space-y-4">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="animate-pulse flex gap-4">
                      <div className="w-24 h-16 bg-primary/10"></div>
                      <div className="flex-1">
                        <div className="h-4 bg-primary/10 w-3/4 mb-2"></div>
                        <div className="h-3 bg-primary/10 w-1/2"></div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : savedArticles.length === 0 ? (
                <div className="text-center py-12">
                  <Bookmark className="w-12 h-12 text-secondary/30 mx-auto mb-4" />
                  <p className="text-secondary font-serif mb-4">No saved articles yet</p>
                  <Link to="/blog">
                    <Button variant="link" className="text-accent">
                      Browse articles <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {savedArticles.map((article) => (
                    <div 
                      key={article.post_id}
                      className="flex gap-4 p-4 bg-background border border-primary/10 hover:border-accent/30 transition-colors"
                      data-testid={`saved-article-${article.post_id}`}
                    >
                      <Link to={`/blog/${article.slug}`} className="shrink-0">
                        <div className="w-24 h-16 overflow-hidden">
                          <img 
                            src={article.featured_image || 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=200'}
                            alt={article.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      </Link>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="label-uppercase text-xs">{article.category}</span>
                          <span className="text-secondary text-xs flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(article.created_at)}
                          </span>
                        </div>
                        <Link to={`/blog/${article.slug}`}>
                          <h3 className="font-sans font-medium text-primary hover:text-accent transition-colors line-clamp-1">
                            {article.title}
                          </h3>
                        </Link>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveArticle(article.post_id)}
                        className="shrink-0 text-secondary hover:text-destructive"
                        data-testid={`remove-article-${article.post_id}`}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-4">
            {/* Newsletter Preferences */}
            <div className="dashboard-panel mb-6">
              <h3 className="text-lg font-sans font-medium text-primary mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-accent" />
                Newsletter
              </h3>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-primary font-sans">Email Updates</p>
                  <p className="text-xs text-secondary">Receive insights & analysis</p>
                </div>
                <Switch
                  checked={newsletterSubscribed}
                  onCheckedChange={handleNewsletterToggle}
                  data-testid="newsletter-toggle"
                />
              </div>
              {newsletterSubscribed && (
                <div className="mt-4 flex items-center gap-2 text-accent text-sm">
                  <MailCheck className="w-4 h-4" />
                  <span>Subscribed</span>
                </div>
              )}
            </div>

            {/* Quick Links */}
            <div className="dashboard-panel">
              <h3 className="text-lg font-sans font-medium text-primary mb-4">Quick Links</h3>
              <div className="space-y-2">
                <Link 
                  to="/blog"
                  className="flex items-center justify-between p-3 bg-background hover:bg-accent/5 transition-colors"
                >
                  <span className="text-sm font-sans">Browse Articles</span>
                  <ChevronRight className="w-4 h-4 text-secondary" />
                </Link>
                <Link 
                  to="/#contact"
                  className="flex items-center justify-between p-3 bg-background hover:bg-accent/5 transition-colors"
                >
                  <span className="text-sm font-sans">Contact</span>
                  <ChevronRight className="w-4 h-4 text-secondary" />
                </Link>
                <button 
                  onClick={logout}
                  className="w-full flex items-center justify-between p-3 bg-background hover:bg-destructive/5 text-destructive transition-colors"
                  data-testid="dashboard-logout-btn"
                >
                  <span className="text-sm font-sans">Sign Out</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
